/**
 * The login (ADR 0034, integration plan slice 7b): an account is a name and a password (Q49). One `center` panel
 * with its title in the corner tab (Q52), the kit's first text fields (the panel's style, 44 px, a name with
 * `autocomplete="username"` and a password with `type="password"` and `autocomplete="current-password"`, or
 * `new-password` while the cursor is on New account, so a password manager offers to make and keep one), and two
 * rows in the one list style: `ui.account.log_in` and `ui.account.create`. While the cursor is on New account a third
 * field asks for the invite code (`ui.login.invite`, ADR 0038 D10: no account without one). Enter in a field acts on
 * the cursor's row; a tap on Log in acts at once (a login screen has nothing to look at first), and a tap on New
 * account moves the cursor there first (its invite field opens and takes the focus), the next tap creates; the pad's
 * up and down move the cursor and its centre confirms (`nav`). While a request is out the rows are disabled. The
 * server's refusal shows as a one-line `Dialogue` over it (`error`, already in the page's language); closing it gives
 * the fields back.
 *
 * Since slice 8h (ADR 0042; Q87) New account also asks for the time zone (`ui.login.time_zone`): a `<select>` in the
 * kit's field style of the zones the shell passes (`timeZones`, IANA names, grouped by the region before the first
 * slash, each shown as the rest of its name with spaces for underscores; a name with no slash, `UTC`, first and
 * ungrouped), the device's zone (`timeZone`) selected at first, and one line under it (`ui.login.time_zone_note`). It
 * shows with the invite field, while the cursor is on New account; logging in ignores it.
 *
 * This is not a `Menu`: a menu takes the keys it reads (Space, E, Backspace) in the capture phase, which typing a
 * name needs. It draws its rows with the menu's classes, so there is still one list style.
 */
import { Fragment, type SyntheticEvent, useEffect, useId, useRef, useState } from 'react';
import type { Strings } from '@ab/session';
import { Dialogue } from './dialogue.tsx';
import type { MenuNav, NavCommand } from './nav.ts';
import { Panel } from './panel.tsx';
import { text } from './text.ts';
import './ui.css';

export type LoginAction = 'log_in' | 'create';

export type LoginProps = {
  readonly strings: Strings;
  /** The tab's title (the game's name). */
  readonly title: string;
  /** The server's refusal as a line in the page's language; null when there is none. */
  readonly error: string | null;
  /** A request is out: the rows wait. */
  readonly busy: boolean;
  /**
   * `invite`: the invite code typed, `timeZone` the zone chosen (creating an account needs both; logging in ignores
   * them).
   */
  readonly onSubmit: (action: LoginAction, name: string, password: string, invite: string, timeZone: string) => void;
  readonly onDismissError: () => void;
  /** The zones New account offers (IANA names; slice 8h), and the one selected at first (the device's). */
  readonly timeZones: readonly string[];
  readonly timeZone: string;
  readonly nav?: MenuNav;
};

const actions: readonly LoginAction[] = ['log_in', 'create'];

/** A zone's name as its option shows it: the part after the region, with spaces for underscores ("Los Angeles"). */
const zoneLabel = (zone: string): string => (zone.includes('/') ? zone.slice(zone.indexOf('/') + 1) : zone).replaceAll('_', ' ');

/** The zones grouped by their region (the part before the first slash), in the order given; names with none first, alone. */
const zoneGroups = (zones: readonly string[]): { readonly region: string | null; readonly zones: readonly string[] }[] => {
  const groups = new Map<string | null, string[]>();
  for (const z of zones) {
    const region = z.includes('/') ? z.slice(0, z.indexOf('/')) : null;
    const list = groups.get(region) ?? [];
    list.push(z);
    groups.set(region, list);
  }
  const loose = groups.get(null) ?? [];
  groups.delete(null);
  return [...(loose.length > 0 ? [{ region: null, zones: loose }] : []), ...[...groups].map(([region, list]) => ({ region, zones: list }))];
};

export const Login = ({ strings, title, error, busy, onSubmit, onDismissError, timeZones, timeZone: deviceZone, nav }: LoginProps) => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [invite, setInvite] = useState('');
  const [timeZone, setTimeZone] = useState(deviceZone);
  const [cursor, setCursor] = useState(0);
  const first = useRef<HTMLInputElement>(null);
  const inviteField = useRef<HTMLInputElement>(null);
  const id = useId();
  const current = useRef({ name, password, invite, timeZone, cursor, busy, error, onSubmit });
  current.current = { name, password, invite, timeZone, cursor, busy, error, onSubmit };
  const submit = (action: LoginAction): void => {
    const c = current.current;
    if (c.busy || c.error !== null) return;
    c.onSubmit(action, c.name, c.password, c.invite, c.timeZone);
  };
  const submitRef = useRef(submit);
  submitRef.current = submit;
  useEffect(() => {
    first.current?.focus({ preventScroll: true });
  }, []);
  // Back from an error: the name field again.
  useEffect(() => {
    if (error === null) first.current?.focus({ preventScroll: true });
  }, [error]);
  // On New account, its invite field takes the focus (the name and the password are usually typed already).
  useEffect(() => {
    if (actions[cursor] === 'create') inviteField.current?.focus({ preventScroll: true });
  }, [cursor]);
  useEffect(() => {
    const act = (command: NavCommand): void => {
      if (current.current.error !== null) return;
      switch (command) {
        case 'up':
        case 'down':
          setCursor((c) => (c + 1) % actions.length);
          return;
        case 'confirm':
          submitRef.current(actions[current.current.cursor] ?? 'log_in');
          return;
        case 'close':
        case 'left':
        case 'right':
          return;
      }
    };
    return nav?.subscribe(act);
  }, [nav]);
  const onForm = (e: SyntheticEvent): void => {
    e.preventDefault();
    submit(actions[current.current.cursor] ?? 'log_in');
  };
  return (
    <>
      <div
        className="ab-veil"
        data-testid="login-veil"
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) e.preventDefault();
        }}
      />
      <Panel placement="center" className="ab-menu ab-login" data-testid="login" data-titled="true" aria-label={title}>
        <Panel placement="tab" className="ab-menu-title" data-testid="login-title">
          {title}
        </Panel>
        <form
          onSubmit={onForm}
          noValidate
          onKeyDown={(e) => {
            // Enter in a field acts on the cursor's row: logs in, or (on New account) creates the account. A form with
            // fields and no submit button would not submit by itself. Never from the time zone's list (slice 8h fix
            // round): Enter there belongs to the list, and a zone not chosen yet must not create the account.
            if (e.key !== 'Enter' || e.target instanceof HTMLSelectElement) return;
            e.preventDefault();
            submit(actions[current.current.cursor] ?? 'log_in');
          }}
        >
          <label className="ab-login-field" htmlFor={`${id}-name`}>
            <span className="ab-login-label">{text(strings, 'ui.account.name')}</span>
            <input
              ref={first}
              id={`${id}-name`}
              className="ab-input"
              data-testid="login-name"
              name="username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={24}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </label>
          <label className="ab-login-field" htmlFor={`${id}-password`}>
            <span className="ab-login-label">{text(strings, 'ui.account.password')}</span>
            <input
              id={`${id}-password`}
              className="ab-input"
              data-testid="login-password"
              name="password"
              type="password"
              autoComplete={actions[cursor] === 'create' ? 'new-password' : 'current-password'}
              maxLength={128}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
            />
          </label>
          {actions[cursor] === 'create' && (
            <label className="ab-login-field" htmlFor={`${id}-invite`}>
              <span className="ab-login-label">{text(strings, 'ui.login.invite')}</span>
              <input
                ref={inviteField}
                id={`${id}-invite`}
                className="ab-input"
                data-testid="login-invite"
                name="invite"
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                maxLength={64}
                value={invite}
                onChange={(e) => {
                  setInvite(e.target.value);
                }}
              />
            </label>
          )}
          {actions[cursor] === 'create' && (
            <label className="ab-login-field" htmlFor={`${id}-zone`}>
              <span className="ab-login-label">{text(strings, 'ui.login.time_zone')}</span>
              <select
                id={`${id}-zone`}
                className="ab-input ab-select"
                data-testid="login-time-zone"
                name="time-zone"
                value={timeZone}
                aria-describedby={`${id}-zone-note`}
                onChange={(e) => {
                  setTimeZone(e.target.value);
                }}
              >
                {zoneGroups(timeZones).map((g) => {
                  const options = g.zones.map((z) => (
                    <option key={z} value={z}>
                      {zoneLabel(z)}
                    </option>
                  ));
                  return g.region === null ? (
                    <Fragment key="">{options}</Fragment>
                  ) : (
                    <optgroup key={g.region} label={g.region}>
                      {options}
                    </optgroup>
                  );
                })}
              </select>
              <span className="ab-login-note" id={`${id}-zone-note`} data-testid="login-time-zone-note">
                {text(strings, 'ui.login.time_zone_note')}
              </span>
            </label>
          )}
        </form>
        <ul role="menu" aria-label={title}>
          {actions.map((action, i) => (
            <li key={action} role="none">
              <button
                type="button"
                role="menuitem"
                className="ab-menu-row"
                data-key={action}
                data-selected={i === cursor}
                aria-current={i === cursor ? 'true' : undefined}
                aria-disabled={busy}
                onClick={() => {
                  // New account opens its invite field first; the next tap creates.
                  const opening = action === 'create' && actions[current.current.cursor] !== 'create';
                  setCursor(i);
                  if (!opening) submit(action);
                }}
              >
                <span className="ab-menu-cursor" aria-hidden="true">
                  {i === cursor ? text(strings, 'ui.menu.cursor') : ''}
                </span>
                <span className="ab-menu-label">{text(strings, `ui.account.${action}`)}</span>
              </button>
            </li>
          ))}
        </ul>
      </Panel>
      {error !== null && <Dialogue strings={strings} speaker={null} text={error} more={false} onAdvance={onDismissError} onClose={onDismissError} {...(nav === undefined ? {} : { nav })} />}
    </>
  );
};

export type NewPasswordProps = {
  readonly strings: Strings;
  /** The server's refusal (a password too short, no answer) as a line in the page's language; null when there is none. */
  readonly error: string | null;
  /** A request is out: the row waits. */
  readonly busy: boolean;
  /** The new password, typed the same twice. */
  readonly onSubmit: (password: string) => void;
  readonly onDismissError: () => void;
  readonly nav?: MenuNav;
};

/**
 * The new password (ADR 0038 D8): an account logged in with a temporary password from the crew chooses its own before
 * the town. The login's window: a `center` panel titled `ui.login.new_password` in its tab, two password fields
 * (`ui.account.password`, `ui.login.again`, `autocomplete="new-password"`) and one row, `ui.login.save`; Enter in a
 * field or the pad's centre saves. Two passwords that differ say `ui.login.mismatch` (nothing is sent); the server's
 * refusal is the `error` line. Either line is a one-line `Dialogue`; closing it gives the fields back.
 */
export const NewPassword = ({ strings, error, busy, onSubmit, onDismissError, nav }: NewPasswordProps) => {
  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  const id = useId();
  const title = text(strings, 'ui.login.new_password');
  const shown = mismatch ? text(strings, 'ui.login.mismatch') : error;
  const current = useRef({ first, second, busy, shown, onSubmit });
  current.current = { first, second, busy, shown, onSubmit };
  const save = (): void => {
    const c = current.current;
    if (c.busy || c.shown !== null) return;
    if (c.first !== c.second) {
      setMismatch(true);
      return;
    }
    c.onSubmit(c.first);
  };
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (shown === null) field.current?.focus({ preventScroll: true });
  }, [shown]);
  useEffect(() => {
    const act = (command: NavCommand): void => {
      if (command === 'confirm') saveRef.current();
    };
    return nav?.subscribe(act);
  }, [nav]);
  const input = (which: 'first' | 'second') => (
    <input
      ref={which === 'first' ? field : undefined}
      id={`${id}-${which}`}
      className="ab-input"
      data-testid={`new-password-${which}`}
      name={which === 'first' ? 'new-password' : 'new-password-again'}
      type="password"
      autoComplete="new-password"
      maxLength={128}
      value={which === 'first' ? first : second}
      onChange={(e) => {
        if (which === 'first') setFirst(e.target.value);
        else setSecond(e.target.value);
      }}
    />
  );
  return (
    <>
      <div
        className="ab-veil"
        data-testid="new-password-veil"
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) e.preventDefault();
        }}
      />
      <Panel placement="center" className="ab-menu ab-login" data-testid="new-password" data-titled="true" aria-label={title}>
        <Panel placement="tab" className="ab-menu-title">
          {title}
        </Panel>
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            save();
          }}
        >
          <label className="ab-login-field" htmlFor={`${id}-first`}>
            <span className="ab-login-label">{text(strings, 'ui.account.password')}</span>
            {input('first')}
          </label>
          <label className="ab-login-field" htmlFor={`${id}-second`}>
            <span className="ab-login-label">{text(strings, 'ui.login.again')}</span>
            {input('second')}
          </label>
        </form>
        <ul role="menu" aria-label={title}>
          <li role="none">
            <button type="button" role="menuitem" className="ab-menu-row" data-key="save" data-selected="true" aria-current="true" aria-disabled={busy} onClick={save}>
              <span className="ab-menu-cursor" aria-hidden="true">
                {text(strings, 'ui.menu.cursor')}
              </span>
              <span className="ab-menu-label">{text(strings, 'ui.login.save')}</span>
            </button>
          </li>
        </ul>
      </Panel>
      {shown !== null && (
        <Dialogue
          strings={strings}
          speaker={null}
          text={shown}
          more={false}
          onAdvance={() => {
            setMismatch(false);
            onDismissError();
          }}
          onClose={() => {
            setMismatch(false);
            onDismissError();
          }}
          {...(nav === undefined ? {} : { nav })}
        />
      )}
    </>
  );
};
